import type { SupabaseClient } from "@supabase/supabase-js";
import type { UserRoleProfile } from "~/composables/useUserRole";
import { resolveAuthProfile } from "~/utils/resolveAuthProfile";

/** Keep middleware profile cache aligned with useUserRole state. */
export function syncProfileToUserRoleState(resolved: UserRoleProfile | null) {
  if (!resolved) return;

  const profile = useState<UserRoleProfile | null>("userRole:profile", () => null);
  const userId = useSupabaseUser().value?.id;

  if (
    profile.value?.role &&
    profile.value.user_id === userId &&
    userId &&
    resolved.user_id !== userId
  ) {
    return;
  }

  profile.value = resolved;
}

export function seedAuthProfileCache(resolved: UserRoleProfile | null) {
  const cache = useState<UserRoleProfile | null | undefined>(
    "middleware:authProfile",
    () => undefined,
  );
  cache.value = resolved;
  syncProfileToUserRoleState(resolved);
}

async function getAccessToken(supabase: SupabaseClient): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function middlewareApiFetch<T>(
  path: string,
  token: string,
  init?: Parameters<typeof $fetch>[1],
): Promise<T> {
  return $fetch<T>(path, {
    ...init,
    headers: {
      ...(init?.headers as Record<string, string> | undefined),
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Resolve the signed-in profile during route middleware without calling `useRoute()`.
 * Caches the result for the current navigation so chained middleware can reuse it.
 */
export async function resolveAuthProfileInMiddleware(
  supabase: SupabaseClient,
  authUserId: string,
  authEmail?: string | null,
): Promise<UserRoleProfile | null> {
  const cache = useState<UserRoleProfile | null | undefined>(
    "middleware:authProfile",
    () => undefined,
  );

  if (cache.value !== undefined) {
    syncProfileToUserRoleState(cache.value);
    return cache.value;
  }

  const token = await getAccessToken(supabase);
  if (token) {
    try {
      const { profile } = await middlewareApiFetch<{ profile: UserRoleProfile }>(
        "/api/me/profile",
        token,
      );
      cache.value = profile ?? null;
      syncProfileToUserRoleState(cache.value);
      return cache.value;
    } catch {
      // Fall through to direct Supabase lookup.
    }
  }

  const profile = await resolveAuthProfile(supabase, authUserId, authEmail);
  cache.value = profile;
  syncProfileToUserRoleState(profile);
  return profile;
}

export function clearMiddlewareAuthProfileCache() {
  const cache = useState<UserRoleProfile | null | undefined>(
    "middleware:authProfile",
    () => undefined,
  );
  cache.value = undefined;
}

export async function middlewareApiGet<T>(
  supabase: SupabaseClient,
  path: string,
): Promise<T> {
  const token = await getAccessToken(supabase);
  if (!token) {
    throw new Error("Unauthorized");
  }
  return middlewareApiFetch<T>(path, token);
}
